import { RuleType } from '@decisions/server/DecisionsFramework/Design/Flow';
import { OutputCell } from './OutputCell';
import { IAllGroupModel } from 'domains/designers/RuleDesigner/models/AllGroupModel';
import { ShowContextEditorLayerCallback } from 'domains/designers/TruthTable/TruthTable';
import { SetValueEventObj } from './OutputCell';
import { SequentialEditSession } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/SequentialEditSession';
import { RuleOutputDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service';
import { RuleExecutionResult } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Events';
import { showSequentialPicker } from 'domains/designers/common/Utils';

export interface IOutputCellsProps {
  rowModel: IAllGroupModel;
  ruleType: RuleType;
  outputs: RuleOutputDisplayData[];
  executionResult: RuleExecutionResult | null;
  getSingleResultContext: (event: { ruleSessionId: string }) => Promise<string>;
  getSingleResultInputMappingSession: (event: {
    ruleSessionId: string;
    stepId: string;
  }) => Promise<SequentialEditSession>;

  getMultipleResultContext: (event: {
    ruleSessionId: string;
    ruleStepId: string;
    resultName: string;
    index: number;
  }) => Promise<string>;
  getMultipleResultInputMappingSession: (event: {
    ruleSessionId: string;
    ruleStepId: string;
    resultName: string;
    index: number;
  }) => Promise<SequentialEditSession>;

  setInlineValue: (event: SetValueEventObj) => void;
  showContextEditorLayer: ShowContextEditorLayerCallback;
}

/**
 * Reusable component for editable outputs. The aim of the component is to not repeat generating
 * outputs several times as it is really easy to forget checking RuleType and miss <OutputCell> for "RuleType.DataReturnRule".
 *
 * Only custom behavior can have RuleType = DataReturnRule.
 */
export function OutputCells({
  rowModel,
  ruleType,
  outputs,
  executionResult,
  setInlineValue,
  showContextEditorLayer,
  getSingleResultContext,
  getSingleResultInputMappingSession,
  getMultipleResultContext,
  getMultipleResultInputMappingSession,
}: IOutputCellsProps) {
  switch (ruleType) {
    case RuleType.DataReturnRule:
      return (
        <OutputCell
          executionResult={executionResult}
          output={outputs[0]}
          model={rowModel}
          onEditClick={e => {
            showContextEditorLayer({
              target: e.target,
              getContext: () => getSingleResultContext({ ruleSessionId: rowModel.RuleSessionId }),
            });
          }}
          setValue={setInlineValue}
          showFullEditor={({ editorInfo }) => {
            if (editorInfo.mappingType !== 'SelectValueInputMapping') {
              return;
            }

            getSingleResultInputMappingSession({
              ruleSessionId: rowModel.RuleSessionId,
              stepId: rowModel.Id,
            }).then(showSequentialPicker);
          }}
        />
      );
    case RuleType.MultipleDataRuleValueOutput:
    case RuleType.MultipleDataRule:
      return (
        <>
          {outputs.map((output, index) => (
            <OutputCell
              key={`${output.DataDescriptionName} ${index}`}
              executionResult={executionResult}
              output={output}
              model={rowModel}
              onEditClick={e => {
                showContextEditorLayer({
                  target: e.target,
                  getContext: () =>
                    getMultipleResultContext({
                      ruleSessionId: rowModel.RuleSessionId,
                      ruleStepId: rowModel.Id,
                      resultName: e.output.DisplayName,
                      index,
                    }),
                });
              }}
              setValue={setInlineValue}
              showFullEditor={({ editorInfo }) => {
                if (editorInfo.mappingType !== 'SelectValueInputMapping') {
                  return;
                }

                getMultipleResultInputMappingSession({
                  index,
                  resultName: output.DisplayName,
                  ruleSessionId: rowModel.RuleSessionId,
                  ruleStepId: rowModel.Id,
                }).then(showSequentialPicker);
              }}
            />
          ))}
        </>
      );
  }

  return null;
}
